import { type EntityUser, type Role, type User } from "@digits-graphql/frontend/graphql-bearer"
import stringHelper from "@digits-shared/helpers/stringHelper"
import type SessionUser from "@digits-shared/session/User"

export interface UserDisplayInfo {
  givenName?: string | null
  familyName?: string | null
  primaryEmailAddress?: string
  emailAddress?: string | null
  avatarUrl?: string | null
}

export enum UserRole {
  NoAccess = "No Access",
  // Distinct from NoAccess: the user has a role we have no label for, which is
  // not the same as having no role at all.
  Unknown = "Unknown",
  // NOTE: Intentionally displaying limited user as associate,
  //       which is distinct from AffiliationAssociate.
  Limited = "Associate",
  AccountManager = "Account Manager",
  Admin = "Admin",
  Accountant = "Accountant",
  Agent = "Digits Agent",
  Managed = "Managed",
  ReadOnly = "Read Only",
  LegalEntityManager = "Legal Entity Manager",
  LegalEntityClient = "Legal Entity Client",
  Demo = "Demo",
}

export enum UserRoleFullyQualified {
  NoAccess = "",
  Limited = "com.digits.roles.organization.limited",
  AccountManager = "com.digits.roles.organization.accountmanager",
  Admin = "com.digits.roles.organization.admin",
  AffiliationAdmin = "com.digits.roles.affiliation.admin",
  AffiliationAccountant = "com.digits.roles.affiliation.accountant",
  AffiliationAssociate = "com.digits.roles.affiliation.associate",
  AffiliationAgent = "com.digits.roles.affiliation.agent",
  Managed = "com.digits.roles.organization.managed",
  ReadOnly = "com.digits.roles.organization.readonly",
  LegalEntityManager = "com.digits.roles.legalentity.manager",
  LegalEntityClient = "com.digits.roles.legalentity.client",
  SandboxAdmin = "com.digits.roles.organization.sandboxadmin",
  SandboxAffiliationAdmin = "com.digits.roles.affiliation.sandboxadmin",
  PartnerEntityClient = "com.gs.roles.entity.client",
}

type DisplayNameOptions = "abbreviateLast"

const userHelper = {
  displayName(user: UserDisplayInfo, ...options: DisplayNameOptions[]) {
    const details = []
    const givenName = user.givenName?.trim()
    const familyName = user.familyName?.trim()

    if (givenName) details.push(givenName)
    if (familyName) {
      if (givenName && options.includes("abbreviateLast")) {
        familyName
          .match(/^\p{L}/u)
          ?.slice(0, 1)
          .forEach((initial) => details.push(`${initial.toLocaleUpperCase()}.`))
      } else {
        details.push(familyName)
      }
    }

    if (!details.length) {
      details.push(user.emailAddress || user.primaryEmailAddress)
    }

    return details.join(" ")
  },

  largeAvatarUrl(user: { avatarUrl?: string | null }, size = 130) {
    if (!user.avatarUrl) return ""
    return user.avatarUrl.replace(/=s\d+-c/, `=s${size * 2}-c`)
  },

  roleDisplayName(role?: Role | string): UserRole {
    const id = typeof role === "object" ? role.id : role
    // An empty id is the NoAccess sentinel, and a Role object carrying it is
    // still truthy — so resolve the id before deciding there's no role at all.
    if (!id) return UserRole.NoAccess

    switch (id) {
      case UserRoleFullyQualified.Admin:
        return UserRole.Admin
      case UserRoleFullyQualified.AccountManager:
        return UserRole.AccountManager
      case UserRoleFullyQualified.Limited:
        return UserRole.Limited
      case UserRoleFullyQualified.AffiliationAdmin:
        return UserRole.Admin
      case UserRoleFullyQualified.AffiliationAccountant:
        return UserRole.Accountant
      case UserRoleFullyQualified.AffiliationAssociate:
        return UserRole.Limited
      case UserRoleFullyQualified.AffiliationAgent:
        return UserRole.Agent
      case UserRoleFullyQualified.Managed:
        return UserRole.Managed
      case UserRoleFullyQualified.ReadOnly:
        return UserRole.ReadOnly
      case UserRoleFullyQualified.LegalEntityManager:
        return UserRole.LegalEntityManager
      case UserRoleFullyQualified.LegalEntityClient:
        return UserRole.LegalEntityClient
      case UserRoleFullyQualified.SandboxAdmin:
      case UserRoleFullyQualified.SandboxAffiliationAdmin:
        return UserRole.Demo
      default:
        // Partner-specific roles (e.g. com.gs.roles.affiliation.focs) have no
        // display mapping; derive one from the namespace + leaf.
        return (userHelper.partnerRoleDisplayName(id) ?? UserRole.Unknown) as UserRole
    }
  },

  // "com.gs.roles.affiliation.focs" -> "GS Focs"
  partnerRoleDisplayName(id: string): string | undefined {
    const [, namespace, leaf] = id.match(/^com\.(?!digits\.)([^.]+)\..*\.([^.]+)$/) ?? []
    if (!namespace || !leaf) return undefined
    return `${namespace.toUpperCase()} ${stringHelper.capitalize(leaf)}`
  },

  legalEntityRoleDisplayName(role?: Role | string): string {
    const id = typeof role === "object" ? role.id : role
    if (!id) return UserRole.NoAccess

    const partnerName = userHelper.partnerRoleDisplayName(id)
    if (partnerName) return partnerName

    const roleName = id.split(".").at(-1)

    return roleName ? stringHelper.capitalize(roleName) : UserRole.NoAccess
  },

  isReviewer(emailAddress?: string | null) {
    const email = emailAddress?.trim().toLowerCase()
    if (!email) return false
    return (process.env.REVIEWER_ACCOUNT_EMAILS || "")
      .split(",")
      .some((reviewerEmail) => reviewerEmail.trim().toLowerCase() === email)
  },

  isDigitsEmployee(user: UserDisplayInfo) {
    const isReviewer = [user.primaryEmailAddress, user.emailAddress].some((emailAddress) =>
      userHelper.isReviewer(emailAddress)
    )
    if (isReviewer) {
      return false
    }
    return (
      (!!user.primaryEmailAddress && !!user.primaryEmailAddress.match(/@digits.com$/)) ||
      (!!user.emailAddress && !!user.emailAddress.match(/@digits.com$/)) ||
      false
    )
  },

  compareUsers(userA: UserDisplayInfo, userB: UserDisplayInfo) {
    if (userA === undefined && userB === undefined) return 0
    if (userA === undefined) return 1
    if (userB === undefined) return -1
    return userHelper
      .displayName(userA)
      .toLocaleLowerCase()
      .localeCompare(userHelper.displayName(userB).toLocaleLowerCase())
  },

  userToEntity(
    user: Pick<User, "id" | "givenName" | "familyName" | "primaryEmailAddress" | "avatarUrl">
  ): EntityUser {
    const { id, givenName, familyName, primaryEmailAddress, avatarUrl } = user
    return {
      id,
      avatarUrl,
      givenName: givenName || "",
      familyName: familyName || "",
      emailAddress: primaryEmailAddress,
    }
  },

  sessionUserToEntity(user: SessionUser): EntityUser {
    const { id, givenName, familyName, emailAddress, avatarUrl } = user
    return {
      id,
      avatarUrl,
      givenName: givenName || "",
      familyName: familyName || "",
      emailAddress,
    }
  },
}
export default userHelper
